import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { Page, NotifyModal } from 'components';
import AutodeskViewer from 'components/AutodeskViewer/AutodeskViewer';
import Header from 'components/Header/Header';
import Reload from 'components/Reload/Reload';

import {
  Col,
  Row,
  Card,
  Button,
  Badge,
  Alert,
  Modal,
} from 'react-bootstrap';

import { FaEye, FaFileAlt, FaTrash } from 'react-icons/fa';

import Api from 'services/Api';

const Models3DDetail = () => {
  const { id } = useParams(); // ID do modelo/objectId
  const history = useHistory();
  const location = useLocation(); // Para acessar os dados passados via state

  // Verificação de permissão
  const userData = JSON.parse(localStorage.getItem('userData'));
  if (userData?.permission?.cooperatives?.read !== true) {
    window.location.replace('/home');
  }

  // Estados
  const [modelData, setModelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [type, setType] = useState({ color: "", status: "" });
  const [status, setStatus] = useState('idle'); // idle | translating | success | failed
  const [progress, setProgress] = useState('');
  const [modelUrn, setModelUrn] = useState('');
  const [pollId, setPollId] = useState(null);
  const [confirmRemoveShow, setConfirmRemoveShow] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // Helpers de alerta (mantém padrão usado em outras telas)
  const handleAlertModalSuccess = (msg) => {
    setType({ color: "#198754", status: msg });
    setModalShow(true);
  };

  const handleAlertModalError = (msg) => {
    setType({ color: "red", status: msg });
    setModalShow(true);
  };

  // Estado para detecção de tema
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const bodyHasDarkClass = document.body.classList.contains('dark-theme');
    const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');

    if (savedTheme) {
      return savedTheme;
    }

    return bodyHasDarkClass || htmlHasDarkClass ? 'dark' : 'light';
  });

  // useEffect para detectar mudanças de tema
  useEffect(() => {
    const handleThemeChange = () => {
      const bodyHasDarkClass = document.body.classList.contains('dark-theme');
      const htmlHasDarkClass = document.documentElement.classList.contains('dark-theme');
      const savedTheme = localStorage.getItem('theme');

      let detectedTheme = 'light';

      if (savedTheme) {
        detectedTheme = savedTheme;
      } else if (bodyHasDarkClass || htmlHasDarkClass) {
        detectedTheme = 'dark';
      }

      setTheme(detectedTheme);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.body, { attributes: true });
    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Carregar dados do modelo
  const loadModelData = async () => {
    try {
      setLoading(true);

      // Primeiro, tentar usar os dados passados via state
      const stateModelData = location.state?.modelData;

      if (stateModelData) {
        setModelData(stateModelData);
        return;
      }

      // Fallback: montar dados mínimos a partir do parâmetro :id (que é o objectId encoded)
      if (id) {
        const decodedObjectId = decodeURIComponent(id);

        // Espera formato: "urn:adsk.objects:os.object:bucketKey/objectKey"
        if (decodedObjectId.startsWith('urn:adsk.objects:os.object:')) {
          const marker = 'os.object:';
          const idx = decodedObjectId.indexOf(marker);
          let bucketKey = '';
          let objectKey = '';
          if (idx !== -1) {
            const rest = decodedObjectId.substring(idx + marker.length);
            const parts = rest.split('/');
            bucketKey = parts.shift() || '';
            objectKey = parts.join('/') || '';
          }

          setModelData({
            objectId: decodedObjectId,
            bucketKey,
            objectKey,
          });
          return;
        }

        // Caso não esteja no formato esperado, sinaliza erro
        setError('Object ID inválido ou ausente.');
        handleAlertModalError('Object ID inválido ou ausente.');
      }
    } catch (error) {
      console.error('Erro ao carregar modelo:', error);
      setError('Erro ao carregar o modelo 3D');
      handleAlertModalError('Erro ao carregar o modelo 3D');
    } finally {
      setLoading(false);
    }
  };

  // Verifica status de tradução do Derivative e inicia tradução se necessário
  const checkTranslateStatus = async (urn) => {
    try {
      const response = await Api.get('/api/v1/translate-status', { params: { urn } });
      const { status: newStatus, progress: newProgress } = response.data || {};
      if (newProgress) setProgress(newProgress);
      if (newStatus === 'success') {
        setStatus('success');
        if (pollId) clearInterval(pollId);
        return 'success';
      } else if (newStatus === 'failed' || newStatus === 'timeout') {
        setStatus('failed');
        if (pollId) clearInterval(pollId);
        return 'failed';
      } else if (newStatus === 'inprogress' || newStatus === 'queued') {
        setStatus('translating');
        return 'translating';
      } else {
        // Quando não há manifest, o backend pode retornar vazio
        setStatus('idle');
        return 'idle';
      }
    } catch (err) {
      // Se 404/sem manifest, fica idle para tentarmos traduzir
      setStatus('idle');
      return 'idle';
    }
  };

  const startTranslation = async ({ bucketKey, objectKey }) => {
    try {
      setStatus('translating');
      setProgress('Iniciando...');
      const res = await Api.post('/api/v1/translate-model', { bucketKey, objectKey });
      if (res?.data?.urn) {
        setModelUrn(res.data.urn);
      }
    } catch (err) {
      setStatus('failed');
      handleAlertModalError('Falha ao iniciar tradução do modelo.');
    }
  };

  const handleManualCheck = async () => {
    if (!modelUrn) return;
    const s = await checkTranslateStatus(modelUrn);
    if (s === 'success') {

    } else if (s === 'failed') {
      handleAlertModalError('Conversão falhou. Tente reiniciar.');
    }
  };

  const handleRestartTranslation = async () => {
    if (!modelData?.bucketKey || !modelData?.objectKey) return;
    await startTranslation({ bucketKey: modelData.bucketKey, objectKey: modelData.objectKey });
  };

  // Quando tivermos modelData, calcula URN e verifica/aciona tradução
  useEffect(() => {
    if (!modelData?.objectId) return;
    const urn = getUrnFromObjectId(modelData.objectId);
    if (!urn) {
      setError('Erro ao converter URN do modelo');
      handleAlertModalError('Erro ao converter URN do modelo');
      return;
    }
    setModelUrn(urn);
    // 1) Checar status atual
    checkTranslateStatus(urn).then((s) => {
      if (s === 'idle') {
        startTranslation({ bucketKey: modelData.bucketKey, objectKey: modelData.objectKey });
      }
    });
  }, [modelData]);

  // Polling enquanto traduz
  useEffect(() => {
    if (!modelUrn) return;
    if (status === 'translating') {
      const id = setInterval(() => checkTranslateStatus(modelUrn), 5000);
      setPollId(id);
      return () => clearInterval(id);
    }
    return undefined;
  }, [status, modelUrn]);

  // Carregar dados quando o componente montar
  useEffect(() => {
    if (id) {
      loadModelData();
    }
  }, [id]);

  // Navegar de volta
  const handleGoBack = () => {
    history.goBack();
  };

  // Baixar arquivo
  const handleDownload = async () => {
    if (!modelData?.bucketKey || !modelData?.objectKey) return;
    try {
      const response = await Api.get(`/api/v1/bucket-download`, { params: { bucketKey: modelData.bucketKey, objectKey: modelData.objectKey } });
      if (response?.data?.url) {
        window.open(response.data.url, '_blank');
      } else {
        handleAlertModalError('URL de download não disponível.');
      }
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      handleAlertModalError('Erro ao baixar arquivo');
    }
  };

  // Remover arquivo
  const handleRemove = async () => {
    if (!modelData?.bucketKey || !modelData?.objectKey) return;
    try {
      setLoading(true);
      try {
        await Api.delete('/api/v1/delete-model', {
          data: { bucketKey: modelData.bucketKey, objectKey: modelData.objectKey }
        });
      } catch (err) {
        // Fallback: alguns backends preferem POST para operações de delete
        await Api.post('/api/v1/delete-model', { bucketKey: modelData.bucketKey, objectKey: modelData.objectKey });
      }
      history.push('/models-3d');
    } catch (error) {
      console.error('Erro ao remover modelo:', error);
      handleAlertModalError('Erro ao remover modelo');
    } finally {
      setLoading(false);
    }
  };

  const openConfirmRemove = () => setConfirmRemoveShow(true);
  const closeConfirmRemove = () => setConfirmRemoveShow(false);

  const confirmRemove = async () => {
    await handleRemove();
    closeConfirmRemove();
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Função para obter token do Forge
  const getFreshToken = async () => {
    try {
      const response = await Api.get('/api/v1/viewer-token');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter token:', error);
      handleAlertModalError('Erro ao obter token de acesso');
      throw error;
    }
  };

  // Extrair URN do objectId para o viewer
  const getUrnFromObjectId = (objectId) => {
    if (!objectId) return null;

    // O objectId tem formato: "urn:adsk.objects:os.object:bucket/filename"
    // Para o viewer, precisamos converter para base64
    try {
      const base64Urn = btoa(objectId).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      return base64Urn;
    } catch (error) {
      console.error('Erro ao converter URN:', error);
      return null;
    }
  };

  return (
    <Page className="p-3 models3d-detail" title="Visualizador de Modelos 3D">
      <Header page="Modelos 3D" />

      {loading && <Reload />}

      <NotifyModal
        type={type}
        show={modalShow}
        onHide={() => setModalShow(false)}
      />

      <Modal
        show={confirmRemoveShow}
        onHide={closeConfirmRemove}
        centered
        contentClassName={theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}
      >
        <Modal.Header className={theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'} closeButton>
          <Modal.Title>Confirmar exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body className={theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'}>
          Tem certeza que deseja remover
          {" "}
          <strong>{modelData?.objectKey}</strong>
          {" "}
          do bucket <strong>{modelData?.bucketKey}</strong>?
          <div className="mt-3">
            <small>Para confirmar, digite o nome do arquivo exatamente:</small>
            <input
              type="text"
              className="form-control mt-2"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={modelData?.objectKey}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className={theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}>
          <Button variant="secondary" onClick={closeConfirmRemove}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmRemove}
            disabled={loading || confirmText !== (modelData?.objectKey || '')}
          >
            {loading && (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            )}
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>

      {error ? (
        <Alert variant="danger" className="text-center">
          <h5>⚠️ {error}</h5>
          <p>Verifique se o modelo existe e tente novamente.</p>
          <Button variant="outline-danger" onClick={handleGoBack}>
            Voltar à lista de modelos
          </Button>
        </Alert>
      ) : modelData ? (
        <>
          {/* Visualizador 3D */}
          <Card className={`mb-2 ${theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}`}>
            <Card.Header className={theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FaEye className="me-2" />
                    Visualizador 3D
                  </h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-2">
                  <Button variant="outline-primary" size="sm" onClick={handleManualCheck} disabled={!modelUrn}>
                    Verificar status
                  </Button>
                  {(status === 'failed' || status === 'idle') && (
                    <Button variant="outline-warning" size="sm" onClick={handleRestartTranslation} disabled={!modelData?.bucketKey || !modelData?.objectKey}>
                      Reiniciar conversão
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className={theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'}>
              {status !== 'success' && (
                <Alert variant="info" className="mb-3">
                  {status === 'idle' && (
                    <span>Preparando visualização...</span>
                  )}
                  {status === 'translating' && (
                    <span>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Convertendo para visualização: {progress || 'em andamento...'}
                    </span>
                  )}
                  {status === 'failed' && (
                    <span>Falha na conversão do modelo. Tente novamente mais tarde.</span>
                  )}
                  <div className="mt-2"><small>Status: {status} {progress && `• Progresso: ${progress}`}</small></div>
                  <div><small>URN: {modelUrn || '-'}</small></div>
                </Alert>
              )}
              {getUrnFromObjectId(modelData.objectId) && status === 'success' ? (
                <div>
                  <AutodeskViewer
                    urn={modelUrn}
                    getFreshToken={getFreshToken}
                    onError={(e) => {
                      console.error('Viewer error:', e);
                      handleAlertModalError(`Erro no viewer: ${e?.message || e?.code || 'desconhecido'}`);
                    }}
                  />
                </div>
              ) : (
                status === 'failed' && (
                  <Alert variant="warning" className="text-center">
                    <h6>⚠️ Não foi possível preparar o modelo para visualização</h6>
                    <p>Tente novamente mais tarde ou verifique o arquivo de origem.</p>
                    <p><small>Object ID: {modelData.objectId}</small></p>
                  </Alert>
                )
              )}
            </Card.Body>
          </Card>

          <Card className={`mb-2 ${theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}`}>
            <Card.Header className={theme === 'dark' ? 'bg-dark text-light border-secondary' : 'bg-light text-dark'}>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FaFileAlt className="me-2" />
                    {modelData.objectKey}
                  </h5>
                </Col>
                <Col xs="auto" className="d-flex align-items-center gap-2">
                  {status && (
                    <Badge bg={status === 'success' ? 'success' : status === 'failed' ? 'danger' : 'warning'} className="me-2">
                      {status === 'success' && 'Pronto'}
                      {status === 'translating' && (progress || 'Convertendo...')}
                      {status === 'idle' && 'Aguardando'}
                      {status === 'failed' && 'Falhou'}
                    </Badge>
                  )}

                  {/* <Button
                    variant="outline-success"
                    size="sm"
                    onClick={handleDownload}
                    className="me-2"
                    disabled={!modelData?.bucketKey || !modelData?.objectKey}
                  >
                    <FaDownload className="me-1" />
                    Download
                  </Button> */}

                  {userData?.master === true && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={openConfirmRemove}
                      disabled={!modelData?.bucketKey || !modelData?.objectKey}
                    >
                      <FaTrash className="me-1" />
                      Remover
                    </Button>
                  )}

                </Col>
              </Row>
            </Card.Header>
            <Card.Body className={theme === 'dark' ? 'bg-dark text-light' : 'bg-light text-dark'}>
              <Row>
                <Col md={6}>
                  <p><strong>Bucket:</strong>
                    <Badge bg="primary" className="ms-2">{modelData.bucketKey}</Badge>
                  </p>
                  <p><strong>Tamanho:</strong> {formatFileSize(modelData.size)}</p>
                </Col>
                <Col md={6}>
                  <p><strong>SHA1:</strong>
                    <code className="ms-2">{modelData.sha1}</code>
                  </p>
                  <p><strong>Object ID:</strong>
                    <small className="ms-2 text-muted">{modelData.objectId}</small>
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

        </>
      ) : null}
    </Page>
  );
};

export default Models3DDetail;
